// client/src/components/SocialInbox.jsx
// Universal Social Inbox — replies, DMs, mentions across all platforms

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchInbox, syncInbox, markRead, sendReply, hideReply } from '../services/inboxApi';
import { PLATFORM_META } from '../services/analyticsApi';
import AppFooter from './AppFooter';
import { FaArrowLeft, FaHome } from 'react-icons/fa';

const TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'reply', label: 'Replies' },
  { value: 'dm', label: 'DMs' },
  { value: 'mention', label: 'Mentions' },
];

const SUPPORTED_PLATFORMS = ['threads', 'facebook', 'instagram', 'youtube', 'bluesky', 'twitter'];
const HIDE_SUPPORTED = ['threads', 'facebook', 'instagram'];

function PlatformIcon({ platform, size = 14 }) {
  const meta = PLATFORM_META[platform];
  if (!meta) return null;
  return <i className={`bi ${meta.icon}`} style={{ color: meta.color, fontSize: size, marginRight: 6 }} />;
}

function SocialInbox() {
  const { tenant } = useParams();
  const navigate = useNavigate();
  const tenantId = Number(tenant);

  // Data
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventItems, setEventItems] = useState([]);

  // Filters
  const [filters, setFilters] = useState({
    platform: '', type: '', event_id: '', event_item_id: '',
    date_from: '', date_to: '', is_read: '', is_replied: '', hide_status: '',
    page: 1, limit: 50,
  });
  const [showFilters, setShowFilters] = useState(false);

  // UI state
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');

  // Load inbox
  const loadInbox = useCallback(async (f) => {
    try {
      setLoading(true);
      setError(null);
      const activeFilters = f || filters;
      const data = await fetchInbox(tenantId, activeFilters);
      if (activeFilters.page > 1) {
        setItems(prev => [...prev, ...(data.items || [])]);
      } else {
        setItems(data.items || []);
      }
      setTotal(data.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [tenantId, filters]);

  // Load events for filter dropdown
  const loadEvents = useCallback(async () => {
    try {
      const res = await fetch(`/api/events/${tenant}/events`, { credentials: 'include' });
      const data = await res.json();
      if (data.success !== false) {
        setEvents(Array.isArray(data) ? data : (data.events || []));
      }
    } catch {}
  }, [tenant]);

  // Load event items when event changes
  const loadEventItems = useCallback(async (eventId) => {
    if (!eventId) { setEventItems([]); return; }
    try {
      const res = await fetch(`/api/events/${tenant}/events/${eventId}/items`, { credentials: 'include' });
      const data = await res.json();
      if (data.success !== false) {
        setEventItems(Array.isArray(data) ? data : (data.items || []));
      }
    } catch { setEventItems([]); }
  }, [tenant]);

  useEffect(() => {
    if (tenantId) {
      loadInbox();
      loadEvents();
    }
  }, [tenantId]);

  // Handle filter change
  const updateFilter = (key, value) => {
    const newFilters = { ...filters, [key]: value, page: 1 };
    if (key === 'event_id') {
      newFilters.event_item_id = '';
      loadEventItems(value);
    }
    setFilters(newFilters);
    setSelected(null);
    loadInbox(newFilters);
  };

  const clearFilters = () => {
    const empty = {
      platform: '', type: '', event_id: '', event_item_id: '',
      date_from: '', date_to: '', is_read: '', is_replied: '', hide_status: '',
      page: 1, limit: 50,
    };
    setFilters(empty);
    setEventItems([]);
    setSelected(null);
    loadInbox(empty);
  };

  const loadMore = () => {
    const next = { ...filters, page: filters.page + 1 };
    setFilters(next);
    loadInbox(next);
  };

  // Sync
  const handleSync = async () => {
    setSyncing(true);
    setStatusMsg('');
    try {
      const data = await syncInbox(tenantId);
      setStatusMsg(`Synced ${data.synced} items${data.errors?.length ? ` (${data.errors.length} errors)` : ''}`);
      loadInbox({ ...filters, page: 1 });
    } catch (err) {
      setStatusMsg(`Sync failed: ${err.message}`);
    } finally {
      setSyncing(false);
    }
  };

  // Select item
  const handleSelect = async (item) => {
    setSelected(item);
    setReplyText('');
    setStatusMsg('');
    if (!item.is_read) {
      try {
        await markRead(tenantId, item.id, true);
        setItems(prev => prev.map(i => i.id === item.id ? { ...i, is_read: 1 } : i));
      } catch {}
    }
  };

  // Reply
  const handleReply = async () => {
    if (!replyText.trim() || !selected) return;
    setSending(true);
    setStatusMsg('');
    try {
      await sendReply(tenantId, selected.id, replyText.trim());
      setReplyText('');
      setStatusMsg('Reply sent');
      setItems(prev => prev.map(i => i.id === selected.id ? { ...i, is_replied: 1 } : i));
      setSelected(prev => prev ? { ...prev, is_replied: 1 } : prev);
    } catch (err) {
      setStatusMsg(`Reply failed: ${err.message}`);
    } finally {
      setSending(false);
    }
  };

  // Hide/unhide
  const handleHide = async (hide) => {
    if (!selected) return;
    try {
      await hideReply(tenantId, selected.id, hide);
      const newStatus = hide ? 'hidden' : 'visible';
      setItems(prev => prev.map(i => i.id === selected.id ? { ...i, hide_status: newStatus } : i));
      setSelected(prev => prev ? { ...prev, hide_status: newStatus } : prev);
      setStatusMsg(hide ? 'Hidden' : 'Unhidden');
    } catch (err) {
      setStatusMsg(`Failed: ${err.message}`);
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleString() : '-';

  if (!tenantId) return <p style={{ color: '#ff6b6b', padding: 16 }}>Invalid tenant</p>;

  const selectStyle = {
    background: 'rgba(255,255,255,0.1)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)',
    borderRadius: 4, padding: '4px 8px', fontSize: '0.8rem', cursor: 'pointer',
  };
  const optionStyle = { background: '#1a1a2e', color: '#fff' };

  const hasActiveFilters = filters.platform || filters.type || filters.event_id || filters.date_from || filters.date_to
    || filters.is_read !== '' || filters.is_replied !== '' || filters.hide_status;

  return (
    <div style={{ paddingBottom: 80 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 600, color: '#fff' }}>Social Inbox</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => setShowFilters(!showFilters)}
            style={{
              background: hasActiveFilters ? '#ffa500' : 'rgba(255,255,255,0.1)',
              color: '#fff', border: hasActiveFilters ? 'none' : '1px solid rgba(255,255,255,0.3)',
              borderRadius: 6, padding: '4px 12px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
            }}
          >
            Filters {hasActiveFilters ? '*' : ''}
          </button>
          <button
            onClick={handleSync}
            disabled={syncing}
            style={{
              background: '#ffa500', color: '#fff', border: 'none', borderRadius: 6,
              padding: '4px 12px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
              opacity: syncing ? 0.6 : 1,
            }}
          >
            {syncing ? 'Syncing...' : 'Sync'}
          </button>
        </div>
      </div>

      {/* Status message */}
      {statusMsg && (
        <div style={{ background: 'rgba(255,165,0,0.15)', border: '1px solid #ffa500', borderRadius: 8, padding: '8px 12px', marginBottom: 12, color: '#ffa500', fontSize: '0.85rem' }}>
          {statusMsg}
          <span onClick={() => setStatusMsg('')} style={{ float: 'right', cursor: 'pointer', fontWeight: 700 }}>&times;</span>
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{ background: 'rgba(255,50,50,0.15)', border: '1px solid #ff4444', borderRadius: 8, padding: '8px 12px', marginBottom: 12, color: '#ff8888', fontSize: '0.85rem' }}>
          {error}
          <span onClick={() => setError(null)} style={{ float: 'right', cursor: 'pointer', fontWeight: 700 }}>&times;</span>
        </div>
      )}

      {/* Filter bar */}
      {showFilters && (
        <div style={{ background: '#2a2a2a', borderRadius: 10, padding: 12, marginBottom: 12 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            {/* Platform */}
            <select value={filters.platform} onChange={e => updateFilter('platform', e.target.value)} style={selectStyle}>
              <option value="" style={optionStyle}>All Platforms</option>
              {SUPPORTED_PLATFORMS.map(p => (
                <option key={p} value={p} style={optionStyle}>{PLATFORM_META[p]?.name || p}</option>
              ))}
            </select>

            {/* Type */}
            <select value={filters.type} onChange={e => updateFilter('type', e.target.value)} style={selectStyle}>
              {TYPE_OPTIONS.map(o => (
                <option key={o.value} value={o.value} style={optionStyle}>{o.label}</option>
              ))}
            </select>

            {/* Event */}
            <select value={filters.event_id} onChange={e => updateFilter('event_id', e.target.value)} style={selectStyle}>
              <option value="" style={optionStyle}>All Events</option>
              {events.map(ev => (
                <option key={ev.id} value={ev.id} style={optionStyle}>{ev.name}</option>
              ))}
            </select>

            {/* Event Item */}
            {filters.event_id && (
              <select value={filters.event_item_id} onChange={e => updateFilter('event_item_id', e.target.value)} style={selectStyle}>
                <option value="" style={optionStyle}>All Items</option>
                {eventItems.map(ei => (
                  <option key={ei.id} value={ei.id} style={optionStyle}>{ei.name}</option>
                ))}
              </select>
            )}

            {/* Date range */}
            <input type="date" value={filters.date_from} onChange={e => updateFilter('date_from', e.target.value)}
              style={{ ...selectStyle, colorScheme: 'dark' }} placeholder="From" />
            <input type="date" value={filters.date_to} onChange={e => updateFilter('date_to', e.target.value)}
              style={{ ...selectStyle, colorScheme: 'dark' }} placeholder="To" />

            {/* Status filters */}
            <select value={filters.is_read} onChange={e => updateFilter('is_read', e.target.value)} style={selectStyle}>
              <option value="" style={optionStyle}>Read/Unread</option>
              <option value="0" style={optionStyle}>Unread</option>
              <option value="1" style={optionStyle}>Read</option>
            </select>

            <select value={filters.is_replied} onChange={e => updateFilter('is_replied', e.target.value)} style={selectStyle}>
              <option value="" style={optionStyle}>Replied/Unreplied</option>
              <option value="0" style={optionStyle}>Unreplied</option>
              <option value="1" style={optionStyle}>Replied</option>
            </select>

            <select value={filters.hide_status} onChange={e => updateFilter('hide_status', e.target.value)} style={selectStyle}>
              <option value="" style={optionStyle}>Visible/Hidden</option>
              <option value="visible" style={optionStyle}>Visible</option>
              <option value="hidden" style={optionStyle}>Hidden</option>
            </select>

            {hasActiveFilters && (
              <button onClick={clearFilters} style={{
                background: 'transparent', color: '#ff8888', border: '1px solid #ff4444',
                borderRadius: 4, padding: '4px 10px', fontSize: '0.75rem', cursor: 'pointer',
              }}>
                Clear
              </button>
            )}
          </div>
          <div style={{ color: '#a8a8a8', fontSize: '0.75rem', marginTop: 6 }}>{total} items</div>
        </div>
      )}

      {/* Loading */}
      {loading && items.length === 0 && (
        <div style={{ textAlign: 'center', padding: '48px 0' }}>
          <div className="spinner-border text-cobalt" role="status"></div>
          <p style={{ marginTop: 12, color: '#a8a8a8' }}>Loading inbox...</p>
        </div>
      )}

      {/* Empty */}
      {!loading && !error && items.length === 0 && (
        <div style={{ textAlign: 'center', padding: '32px 0', color: '#a8a8a8' }}>
          <h5 style={{ color: '#e6e6e6' }}>Inbox Empty</h5>
          <p style={{ fontSize: '0.85rem' }}>
            {hasActiveFilters
              ? 'No items match your filters. Try broadening your search.'
              : 'Click Sync to fetch replies from your connected platforms.'}
          </p>
        </div>
      )}

      {/* Two-panel layout: list + detail */}
      {items.length > 0 && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {/* List panel */}
          <div style={{
            flex: selected ? '0 0 40%' : '1 1 100%',
            minWidth: 280,
            background: '#2a2a2a', borderRadius: 10, overflow: 'hidden',
          }}>
            <div style={{ padding: '6px 12px', background: '#253866' }}>
              <h6 style={{ margin: 0, fontSize: '0.85rem', color: '#fff', fontWeight: 600 }}>
                {total} item{total !== 1 ? 's' : ''}
              </h6>
            </div>
            <div style={{ maxHeight: 500, overflowY: 'auto' }}>
              {items.map(item => {
                const isActive = selected?.id === item.id;
                const isUnread = !item.is_read;
                return (
                  <div
                    key={item.id}
                    className="inbox-item"
                    onClick={() => handleSelect(item)}
                    style={{
                      padding: '10px 12px',
                      borderBottom: '1px solid #333',
                      cursor: 'pointer',
                      background: isActive ? 'rgba(255,165,0,0.15)' : 'transparent',
                      borderLeft: isActive ? '3px solid #ffa500' : '3px solid transparent',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                      <PlatformIcon platform={item.platform} size={16} />
                      {isUnread && (
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#ffa500', flexShrink: 0 }} />
                      )}
                      <strong style={{ color: '#ffa500', fontSize: '0.8rem' }}>@{item.author_username || 'unknown'}</strong>
                      <span style={{
                        background: 'rgba(255,255,255,0.1)', color: '#a8a8a8', fontSize: '0.65rem',
                        padding: '1px 5px', borderRadius: 3, textTransform: 'uppercase',
                      }}>
                        {item.type}
                      </span>
                      {item.is_replied ? (
                        <i className="bi bi-check-circle-fill" style={{ color: '#4CAF50', fontSize: 12 }} title="Replied" />
                      ) : null}
                      {item.hide_status === 'hidden' && (
                        <span style={{ background: 'rgba(255,165,0,0.2)', color: '#ffa500', fontSize: '0.6rem', padding: '1px 4px', borderRadius: 3 }}>HIDDEN</span>
                      )}
                    </div>
                    <p style={{
                      color: '#e6e6e6', fontSize: '0.8rem', margin: 0,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      fontWeight: isUnread ? 600 : 400,
                    }}>
                      {item.text || '(media)'}
                    </p>
                    <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                      {item.event_name && (
                        <small style={{ color: '#8888ff', fontSize: '0.7rem' }}>
                          <i className="bi bi-calendar3 mr-1" />{item.event_name}
                          {item.event_item_name ? ` / ${item.event_item_name}` : ''}
                        </small>
                      )}
                      <small style={{ color: '#a8a8a8', fontSize: '0.7rem', marginLeft: 'auto' }}>
                        {formatDate(item.platform_timestamp)}
                      </small>
                    </div>
                  </div>
                );
              })}
            </div>
            {items.length < total && (
              <div style={{ textAlign: 'center', padding: 10 }}>
                <button
                  onClick={loadMore}
                  disabled={loading}
                  style={{
                    background: '#253866', color: '#fff', border: '1px solid #ffa500',
                    borderRadius: 6, padding: '6px 16px', fontSize: '0.8rem', cursor: 'pointer',
                  }}
                >
                  {loading ? '...' : 'Load More'}
                </button>
              </div>
            )}
          </div>

          {/* Detail panel */}
          {selected && (
            <div style={{ flex: '1 1 55%', minWidth: 300, background: '#2a2a2a', borderRadius: 10, overflow: 'hidden' }}>
              {/* Detail header */}
              <div style={{ padding: '8px 12px', background: '#253866', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <PlatformIcon platform={selected.platform} size={18} />
                <span style={{ color: '#fff', fontWeight: 600, fontSize: '0.9rem' }}>
                  {PLATFORM_META[selected.platform]?.name || selected.platform}
                </span>
                {selected.event_name && (
                  <>
                    <span style={{ color: 'rgba(255,255,255,0.4)' }}>|</span>
                    <span style={{ color: '#8888ff', fontSize: '0.8rem' }}>
                      <i className="bi bi-calendar3 mr-1" />{selected.event_name}
                    </span>
                  </>
                )}
                {selected.event_item_name && (
                  <>
                    <span style={{ color: 'rgba(255,255,255,0.4)' }}>/</span>
                    <span style={{ color: '#8888ff', fontSize: '0.8rem' }}>{selected.event_item_name}</span>
                  </>
                )}
                <span style={{ color: '#a8a8a8', fontSize: '0.75rem', marginLeft: 'auto' }}>
                  {formatDate(selected.platform_timestamp)}
                </span>
              </div>

              <div style={{ padding: 12 }}>
                {/* Original post context */}
                {selected.post_content && (
                  <div style={{
                    background: 'rgba(255,255,255,0.05)', borderRadius: 8, padding: '8px 10px',
                    marginBottom: 12, borderLeft: '3px solid #555',
                  }}>
                    <small style={{ color: '#a8a8a8', display: 'block', marginBottom: 4 }}>Original post:</small>
                    <p style={{ color: '#ccc', fontSize: '0.85rem', margin: 0, whiteSpace: 'pre-wrap' }}>
                      {selected.post_content.length > 300 ? selected.post_content.slice(0, 300) + '...' : selected.post_content}
                    </p>
                  </div>
                )}

                {/* Reply/DM body */}
                <div style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <strong style={{ color: '#ffa500' }}>@{selected.author_username || 'unknown'}</strong>
                    <span style={{
                      background: 'rgba(255,255,255,0.1)', color: '#a8a8a8', fontSize: '0.65rem',
                      padding: '1px 5px', borderRadius: 3, textTransform: 'uppercase',
                    }}>
                      {selected.type}
                    </span>
                    {selected.hide_status === 'hidden' && (
                      <span style={{ background: 'rgba(255,165,0,0.2)', color: '#ffa500', fontSize: '0.7rem', padding: '1px 6px', borderRadius: 4 }}>HIDDEN</span>
                    )}
                  </div>
                  <p style={{ color: '#e6e6e6', fontSize: '0.9rem', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {selected.text || '(media only)'}
                  </p>
                  {selected.media_url && (
                    <img src={selected.media_url} alt="Media" style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8, marginTop: 8 }} />
                  )}
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                  {HIDE_SUPPORTED.includes(selected.platform) && (
                    <button
                      onClick={() => handleHide(selected.hide_status !== 'hidden')}
                      style={{
                        background: selected.hide_status === 'hidden' ? 'rgba(0,200,0,0.15)' : 'rgba(255,165,0,0.15)',
                        color: selected.hide_status === 'hidden' ? '#4CAF50' : '#ffa500',
                        border: `1px solid ${selected.hide_status === 'hidden' ? '#4CAF50' : '#ffa500'}`,
                        borderRadius: 6, padding: '4px 12px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
                      }}
                    >
                      {selected.hide_status === 'hidden' ? 'Unhide' : 'Hide'}
                    </button>
                  )}
                </div>

                {/* Reply input */}
                <div style={{ borderTop: '1px solid #444', paddingTop: 12 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                    <textarea
                      value={replyText}
                      onChange={e => setReplyText(e.target.value)}
                      placeholder="Write a reply..."
                      maxLength={500}
                      disabled={sending}
                      style={{
                        flex: 1, background: '#1a1a2e', color: '#fff', border: '1px solid #444',
                        borderRadius: 6, padding: '8px 10px', fontSize: '0.85rem', resize: 'none',
                        minHeight: 48, fontFamily: 'inherit',
                      }}
                      rows={2}
                    />
                    <button
                      onClick={handleReply}
                      disabled={!replyText.trim() || sending}
                      style={{
                        background: '#ffa500', color: '#fff', border: 'none', borderRadius: 6,
                        padding: '8px 16px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
                        opacity: (!replyText.trim() || sending) ? 0.5 : 1, whiteSpace: 'nowrap',
                      }}
                    >
                      {sending ? '...' : 'Reply'}
                    </button>
                  </div>
                  <small style={{ color: '#a8a8a8', fontSize: '0.75rem', marginTop: 4, display: 'block' }}>
                    {replyText.length}/500
                  </small>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <AppFooter
        buttons={[
          { icon: <FaArrowLeft />, label: 'Back', onClick: () => navigate(-1) },
          { icon: <FaHome />, label: 'Home', onClick: () => navigate(`/${tenant}/dashboard`) },
        ]}
      />
    </div>
  );
}

export default SocialInbox;
